import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import ProviderBottomNav from '../../components/common/ProviderBottomNav';
import { Send, ArrowLeft, Headphones } from 'lucide-react';

const API = `${window.location.origin}/api`;

export default function ProviderSupport() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/support-chat/my/messages`, { headers });
      setMessages(res.data.messages || []);
    } catch (e) { /* ignore */ }
  }, [token]);

  useEffect(() => {
    fetchMessages();
    const t = setInterval(fetchMessages, 3000);
    return () => clearInterval(t);
  }, [fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const content = input.trim();
    if (!content) return;
    setSending(true);
    setInput('');
    const optimistic = { id: `tmp-${Date.now()}`, sender_type: 'provider', content, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    try {
      await axios.post(`${API}/support-chat/my/messages`, { content }, { headers });
      fetchMessages();
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col pb-20">
      {/* Header */}
      <div className="bg-slate-900 text-white px-4 pt-12 pb-4 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate('/provider')} data-testid="support-back-btn" className="p-1">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center overflow-hidden">
          <img src="/sidekick.png" alt="Juan" className="w-full h-full object-cover object-top" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
        <div>
          <p className="font-semibold" data-testid="support-title">HandyBayani Support · powered by Juan</p>
          <p className="text-xs text-slate-300">Juan answers instantly — our team can step in anytime</p>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-slate-400 mt-16" data-testid="support-empty">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center overflow-hidden">
              <img src="/sidekick.png" alt="Juan" className="w-full h-full object-cover object-top" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
            <p className="text-sm font-medium text-slate-500">Hi, I'm Juan 👋</p>
            <p className="text-sm mt-1">Ask me anything about running your HandyBayani business — bookings, payouts, your profile and more. I'll bring in a human teammate if you need one.</p>
          </div>
        ) : messages.map((m) => {
          const mine = m.sender_type !== 'staff';
          const isAi = m.is_ai;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              {!mine && (
                isAi ? (
                  <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center overflow-hidden mr-2 mt-1 flex-shrink-0">
                    <img src="/sidekick.png" alt="" className="w-full h-full object-cover object-top" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                ) : (
                  <div className="w-7 h-7 rounded-full bg-slate-800 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
                    <Headphones className="w-3.5 h-3.5 text-amber-400" />
                  </div>
                )
              )}
              <div className={`max-w-[78%] rounded-2xl px-4 py-2 ${mine ? 'bg-amber-500 text-slate-900 rounded-br-sm' : isAi ? 'bg-amber-50 border border-amber-200 text-slate-800 rounded-bl-sm' : 'bg-white border border-stone-200 text-slate-800 rounded-bl-sm'}`}>
                {!mine && <p className="text-xs font-medium text-amber-600 mb-0.5">{m.sender_name || 'Support'}</p>}
                <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
                <p className={`text-[10px] mt-1 ${mine ? 'text-slate-700/70' : 'text-slate-400'}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div className="fixed bottom-16 left-0 right-0 p-3 border-t border-stone-200 bg-white flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
          placeholder="Ask Juan or our team..."
          className="flex-1 h-11 px-4 bg-stone-100 rounded-full text-sm focus:outline-none"
          data-testid="support-message-input"
        />
        <button
          onClick={send}
          disabled={sending || !input.trim()}
          className="w-11 h-11 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center"
          data-testid="support-send-btn"
        >
          <Send className="w-5 h-5 text-white" />
        </button>
      </div>

      <ProviderBottomNav />
    </div>
  );
}
